import * as React from 'react';

import { Input } from '../../../../../Components/Common/Input';
import { maxQuantity } from '../../../../../ReactComponents/QuantityUtils';
import { SetQuantityResult } from '../../../../Checkout/Summary/LineItemsApi';
import { CartItemViewModel } from '../../ViewModels/CartItemViewModel.csharp';

interface ItemQuantityInputProps {
    item: CartItemViewModel;
    setQuantity: (productCode: string, quantity: number) => Promise<SetQuantityResult>;
    isDisabled?: boolean;
    onRequestTimeoutStart?: () => void;
    onRequestTimeoutEnd?: () => void;
}

interface ItemQuantityInputState {
    value: number | '';
}

class ItemQuantityInput extends React.Component<ItemQuantityInputProps, ItemQuantityInputState> {
    setQuantityTimeout: NodeJS.Timeout | number | null = null;

    constructor(props: ItemQuantityInputProps) {
        super(props);
        this.state = {
            value: props.item.quantityOrdered
        };
    }

    componentDidUpdate(prevProps: ItemQuantityInputProps) {
        if (prevProps.item.quantityOrdered != this.props.item.quantityOrdered) {
            this.setState({ value: this.props.item.quantityOrdered });
        }
    }

    render() {
        const { value } = this.state;

        return (
            <div className="ItemQuantityInput">
                <button
                    className="ItemQuantityInput__Button ItemQuantityInput__Button--Remove"
                    onClick={this.decreaseQuantity}
                    disabled={this.shouldDecreaseBeDisabled()}
                >
                    <span className="visuallyHidden">-</span>
                </button>
                <Input
                    type="number"
                    value={value}
                    interval={300}
                    disabled={this.props.isDisabled}
                    onChange={this.handleInput}
                    onFocus={(e) => e.target.select()}
                    onAfterChange={() => this.changeQuantity(this.state.value)}
                />
                <button
                    className="ItemQuantityInput__Button ItemQuantityInput__Button--Add"
                    onClick={this.increaseQuantity}
                    disabled={this.shouldIncreaseBeDisabled()}
                >
                    <span className="visuallyHidden">+</span>
                </button>
            </div>
        );
    }

    private shouldIncreaseBeDisabled = (): boolean => {
        return this.props.isDisabled || (!this.props.isDisabled && this.state.value !== '' && this.state.value >= this.props.item.maxQuantity);
    };

    private shouldDecreaseBeDisabled = (): boolean => {
        return this.props.isDisabled || (!this.props.isDisabled && this.state.value !== '' && this.state.value <= this.props.item.minQuantity);
    };

    private handleInput = (value: string) => {
        const parsedValue = parseInt(value);
        if (parsedValue <= maxQuantity) this.clearSetQuantityTimeout();

        if (value === '' || (parsedValue > 0 && parsedValue <= maxQuantity)) {
            this.setState({ value: parsedValue || '' });
        }
    };

    private changeQuantity = async (value: number | '') => {
        if (value !== '' && this.props.item.quantityOrdered !== value) {
            if (this.props.onRequestTimeoutStart) {
                this.props.onRequestTimeoutStart();
            }

            const result = await this.props.setQuantity(this.props.item.code, value);
            if (!result.succeeded) {
                this.setState({ value: this.props.item.quantityOrdered });
            } else {
                this.setState({ value: result.quantity });
            }

            if (this.props.onRequestTimeoutEnd) {
                this.props.onRequestTimeoutEnd();
            }
        }
    };

    private increaseQuantity = () => {
        const value = this.state.value;
        if (value !== '') {
            this.setQuantityWithTimeout(value + 1);
        }
    };

    private decreaseQuantity = () => {
        const value = this.state.value;
        if (value !== '' && value > 1) {
            this.setQuantityWithTimeout(value - 1);
        }
    };

    private setQuantityWithTimeout = (value: number) => {
        this.setState({ value });

        this.clearSetQuantityTimeout();
        this.setQuantityTimeout = setTimeout(() => {
            this.changeQuantity(value);
        }, 500);
    };

    private clearSetQuantityTimeout = () => {
        clearTimeout(this.setQuantityTimeout as number);
        this.setQuantityTimeout = null;
    };
}

export default ItemQuantityInput;
